import { DestroyRef, inject, Inject, Injectable } from '@angular/core';
import { EnvironmentType } from '@libs/models/environment.type';
import { BehaviorSubject, Observable } from 'rxjs';
import { NavigationEnd, Router } from '@angular/router';
import { distinctUntilChanged, filter, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/common';
import { ENVIRONMENT } from '@libs/shared/injections/window';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  private isShortFooter$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private isWebSite$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private destroyRef: DestroyRef = inject(DestroyRef);

  constructor(
    @Inject(ENVIRONMENT) private environment: EnvironmentType,
    @Inject(DOCUMENT) private document: Document,
    private router: Router,
  ) {
    this.activeNavigationEvents();
  }

  get isShortFooter(): Observable<boolean> {
    return this.isShortFooter$.asObservable();
  }

  get isWebSite(): Observable<boolean> {
    return this.isWebSite$.asObservable();
  }

  public initHelpersScripts(): void {
    const { initSeon, initRecaptcha, gtmId, fbPixelId } = this.environment;
    const scriptLoaders: { condition: boolean; loader: () => void }[] = [
      { condition: initSeon, loader: () => this.loadScript('https://cdn.seondf.com/js/v5/agent.js') },
      {
        condition: initRecaptcha,
        loader: () => this.loadScript(`https://www.google.com/recaptcha/enterprise.js?render=${this.environment.recaptchaKEY_CODE}`),
      },
      { condition: Boolean(gtmId), loader: () => this.initGoogleTagManager(gtmId) },
      { condition: Boolean(gtmId), loader: () => this.initNoscriptTag(gtmId) },
      { condition: Boolean(fbPixelId), loader: () => this.initFacebookPixel(fbPixelId) },
      { condition: Boolean(fbPixelId), loader: () => this.addFacebookTrackingScript(fbPixelId) },
    ];

    scriptLoaders.forEach(({ condition, loader }) => {
      if (condition) {
        loader();
      }
    });
  }

  private activeNavigationEvents(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        distinctUntilChanged(),
        tap((event: NavigationEnd) => this.updatePageStates(event.urlAfterRedirects)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  private updatePageStates(url: string): void {
    const shortFooterPages = ['main', 'form', 'system', 'verification', 'bankid'];
    const webPages = [
      'home',
      'questions',
      'contacts',
      'documents',
      'blog',
      'promotions',
      'cooperation',
      '404',
      'borrow',
      'repay',
      'about-us',
    ];
    const cleanUrl = url.replace('/ru', '/').split('?')[0];
    const value = cleanUrl === '/' || webPages.some((route: string) => cleanUrl.includes(route));
    this.isShortFooter$.next(shortFooterPages.some((page) => cleanUrl.includes(page)));
    this.isWebSite$.next(value);
  }

  private loadScript(src: string): void {
    const script = this.document.createElement('script');
    script.src = src;
    script.defer = true;
    script.type = 'text/javascript';
    this.document.head.appendChild(script);
  }

  private initGoogleTagManager(id: string): void {
    const script = this.document.createElement('script');
    script.textContent = `(function (w, d, s, l, i) {
          w[l] = w[l] || [];
          w[l].push({ 'gtm.start': new Date().getTime(), event: 'gtm.js' });
          var f = d.getElementsByTagName(s)[0],
            j = d.createElement(s),
            dl = l != 'dataLayer' ? '&l=' + l : '';
          j.async = true; // Використання async замість defer
          j.src = 'https://www.googletagmanager.com/gtm.js?id=' + i + dl;
          f.parentNode.insertBefore(j, f);
        })(window, document, 'script', 'dataLayer', '${id}');`;
    script.defer = true;
    script.type = 'text/javascript';
    this.document.head.appendChild(script);
  }

  private initNoscriptTag(id: string): void {
    const noscript = this.document.createElement('script');
    const iframe = this.document.createElement('iframe');

    iframe.src = `https://www.googletagmanager.com/ns.html?id=${id}`;
    iframe.height = '0';
    iframe.width = '0';
    iframe.style.display = 'none';
    iframe.style.visibility = 'hidden';

    noscript.appendChild(iframe);
    this.document.body.insertAdjacentElement('afterbegin', noscript);
  }

  private initFacebookPixel(id: string): void {
    const script = this.document.createElement('script');
    script.textContent = `!function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};
      if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
      n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t,s)}(window, document,'script',
      'https://connect.facebook.net/en_US/fbevents.js');
    fbq('init', '${id}');
    fbq('track', 'PageView');`;
    script.defer = true;
    script.type = 'text/javascript';
    this.document.head.appendChild(script);
  }

  private addFacebookTrackingScript(id: string): void {
    const noscriptTag = this.document.createElement('noscript');
    noscriptTag.innerHTML = `
      <img height="1"
           src="https://www.facebook.com/tr?id=${id}&ev=PageView&noscript=1"
           style="display: none"
           width="1" />
    `;
    this.document.body.appendChild(noscriptTag);
  }
}
